import { GenericModelApi } from "./GenericModelApi";
import { ApiClient, ApiError, ApiSchema, FetchResponse } from "./ApiClient";
import { QuoteFormData, QuoteRelatedAttributes } from "@/formTypes/QuoteDataType";
import { QuoteFormDataKeys } from "@/generated/QuoteFormDataKeys";
import { QuoteProductApi, QuoteProductDataType } from "./QuoteProductApi";
import { generateNextNumberByModel, omitProperties } from "@utils/Utils";
import { DocumentAutoGeneratedNumberApi } from "./DocumentAutoGeneratedNumberApi";
import { getTenantId, stampCreatorUser } from "@/utils/Auth";

export type QuoteDataType = Partial<ApiSchema["Quote"]["type"] & QuoteFormData>;

class Api extends GenericModelApi<"Quote", QuoteDataType> {
    protected userSortKeyDateField = "fecha" as const

    /**
     * Cuenta las cotizaciones creadas en un mes ("YYYY-MM") para validar el tope
     * mensual del plan en el frontend (las cotizaciones no pasan por save-edocument).
     */
    async countByMonth(monthPrefix: string): Promise<number> {
        const tenantId = await getTenantId();
        let count = 0;
        let nextToken: string | undefined = undefined;
        do {
            const { data, nextToken: nt, errors } = await ApiClient.models["Quote"].listQuoteByFechaTenant(
                { owner: tenantId, fecha: { beginsWith: monthPrefix } },
                { selectionSet: ["id"], limit: 1000, nextToken },
            );
            if (errors) {
                throw new ApiError("Error al contar cotizaciones del mes", "API_ERROR", errors);
            }
            count += (data || []).length;
            nextToken = nt || undefined;
        } while (nextToken);
        return count;
    }

    async fetchAll(filters: any = {}, token?: string, limit: number = 20): Promise<FetchResponse<QuoteDataType>> {
        const selectionSet = [
            ...QuoteFormDataKeys.filter(k => !QuoteRelatedAttributes.includes(k)),
            "currency.*",
            "client.*",
            "quoteProducts.*"
        ]

        return super.fetchAll(filters, token, limit, selectionSet);
    }

    async createQuoteAndProducts(input: QuoteDataType, products: QuoteProductDataType[]): Promise<QuoteDataType> {
        let generatedNumber = await generateNextNumberByModel("Quote");
        if (!generatedNumber?.number) {
            throw Error(`No fue posible generar el identificador de la Cotizacion`)
        }

        const { data, errors } = await ApiClient.models["Quote"].create(
            {
                // No pasa por `this.create()`, así que el creador se estampa aquí. Después del
                // spread: al duplicar una cotización el `input` arrastra el `user` del origen.
                ...await stampCreatorUser(input),
                numero: generatedNumber?.number
            });
        if (!errors && data && data?.id) {
            const resultQuoteProduct = await Promise.all(
                products.map((p) => {
                    let product = omitProperties({ ...p }, ["id", "forceRecalculatePrice", "toDelete"], true);
                    product.quoteId = data.id
                    if (product.exemption && typeof product.exemption == "object") {
                        product.exemption = JSON.stringify(product.exemption);
                    }
                    return QuoteProductApi.create(product);
                })
            )
        } else if (errors) {
            throw new ApiError(`Errores al crear ${String(this.modelName)}`, "API_ERROR", errors);
        }

        await DocumentAutoGeneratedNumberApi.save(generatedNumber)

        return data || {} as QuoteDataType;
    }

    async updateQuoteAndProducts(input: QuoteDataType, products: QuoteProductDataType[]): Promise<QuoteDataType> {
        // `user` y `fecha` son la llave de ordenamiento compuesta del indice
        // `quotesByOwnerAndUserAndFecha`: AppSync exige mandar ambas o ninguna. Al editar no se
        // toca la fecha original (el form manda `getCurrentDateTime()`), asi que tampoco se manda
        // `user`; eso ademas conserva al creador original en lugar de reasignarlo a quien edita.
        delete input.fecha
        delete input.user
        const { data, errors } = await ApiClient.models["Quote"].update(input);
        if (!errors && data && data?.id) {
            const resultQuoteProduct = await Promise.all(
                products.map((p) => {
                    let product = { ...p };
                    if (product.preSavedId) {
                        product.id = product.preSavedId;
                        delete product.preSavedId;
                    } else {
                        delete product.id;
                    }

                    if (product.toDelete && product.id) {
                        return QuoteProductApi.delete(product.id);
                    }

                    product = omitProperties(product, ["forceRecalculatePrice", "toDelete", "selected", "descriptionFact", "priceFact", "subtotalFact", "descuentoFact", "impuestosFact", "impuestos_servicioFact"])

                    product.quoteId = data.id
                    if (product.exemption && typeof product.exemption == "object") {
                        product.exemption = JSON.stringify(product.exemption);
                    }
                    if (product.id) {
                        return QuoteProductApi.update(product.id, product);
                    } else {
                        return QuoteProductApi.create(product);
                    }
                })
            )
        } else if (errors) {
            throw new ApiError(`Errores al crear ${String(this.modelName)}`, "API_ERROR", errors);
        }

        return data || {} as QuoteDataType;
    }
}
export const QuoteApi = new Api("Quote", "numero", "DESC", "numero")